import { Camera, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import BackButton from "./common/BackButton";
import DocumentationManagement from "./DocumentationManagement";
import PlanManagement from "./PlanManagement";

const SectionDetail = ({
  section,
  projectId,
  userRole,
  isGlobalAdmin,
  onBack,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState(section);

  // Determine active tab from URL
  const getActiveTabFromUrl = () => {
    if (location.pathname.includes("/documentation")) {
      return "documentation";
    }
    return "plan"; // default
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());
  // Check if user can manage plans (admin or manager)
  const canManage =
    isGlobalAdmin || userRole === "admin" || userRole === "manager";

  const tabs = [
    { id: "plan", label: t("section_detail.tabs.plan"), icon: FileText },
    {
      id: "documentation",
      label: t("section_detail.tabs.documentation"),
      icon: Camera,
    },
  ];

  useEffect(() => {
    setSectionData(section);
  }, [section]);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    if (tabId === "documentation") {
      navigate(
        `/project/${projectId}/sections/${sectionData._id}/documentation`,
        {
          replace: true,
        }
      );
    } else {
      // Default to plan tab
      navigate(`/project/${projectId}/sections/${sectionData._id}`, {
        replace: true,
      });
    }
  };

  // const handleOpenDocumentation = (frameReference, objectId, documentationId) => {
  //   const params = new URLSearchParams();
  //   if (frameReference) {
  //     params.set("frame", frameReference);
  //   }
  //   if (objectId) {
  //     params.set("object", objectId);
  //   }
  //   let url = `/project/${projectId}/sections/${sectionData._id}/documentation/${documentationId}`;
  //   const paramString = params.toString();
  //   if (paramString) {
  //     url += `?${paramString}`;
  //   }
  //   window.open(url, "_blank");
  // };

  if (!sectionData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BackButton
                label={t("section_detail.back_to_sections")}
                onClick={onBack}
              />
              <div className="flex items-center space-x-2">
                {sectionData.plan && sectionData.plan.planId ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                    Plan uploaded
                    {sectionData.plan.fileName &&
                      sectionData.plan.dwgFileName &&
                      " (PDF & DWG)"}
                    {sectionData.plan.fileName &&
                      !sectionData.plan.dwgFileName &&
                      " (PDF)"}
                    {!sectionData.plan.fileName &&
                      sectionData.plan.dwgFileName &&
                      " (DWG)"}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></span>
                    No plan uploaded
                  </span>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {sectionData.name}
              </h2>
              {sectionData.description && (
                <p className="text-gray-500 mt-1">{sectionData.description}</p>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="flex space-x-1">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center px-6 py-3 font-medium text-sm transition-all duration-150 border-b-2 ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600 bg-blue-50/50"
                        : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50/50"
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="py-6">
            {/* Plan Tab */}
            {activeTab === "plan" && (
              <PlanManagement
                sectionData={sectionData}
                projectId={projectId}
                canManage={canManage}
                onSectionUpdate={setSectionData}
              />
            )}

            {/* Documentation Tab */}
            {activeTab === "documentation" && (
              <DocumentationManagement
                projectId={projectId}
                sectionId={sectionData._id}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionDetail;
